/**
 * Custom Voice Commands Manager
 * Admin UI for creating and managing tenant-specific voice commands
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { 
  Mic, Plus, Pencil, Trash2, Save, X, 
  ChevronDown, ChevronUp, Sparkles, Target,
  ArrowRight, Search, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import agenticApi from '../../api/agenticApi';

const CustomCommandsManager = ({ headers }) => {
  const [commands, setCommands] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  // Form state
  const [form, setForm] = useState({
    name: '',
    patterns: [''],
    action: 'navigate',
    target: '',
    description: '',
    params: {}
  });

  useEffect(() => {
    loadCommands();
    loadSuggestions();
  }, []);

  const loadCommands = async () => {
    try {
      setLoading(true);
      const response = await agenticApi.getCustomCommands(false, headers);
      setCommands(response.data.commands || []);
    } catch (err) {
      console.error('Failed to load commands:', err);
      toast.error('Failed to load custom commands');
    } finally {
      setLoading(false);
    }
  };

  const loadSuggestions = async () => {
    try {
      const response = await agenticApi.getCommandSuggestions(headers);
      setSuggestions(response.data.suggestions || []);
    } catch (err) {
      console.error('Failed to load suggestions:', err);
    }
  };

  const resetForm = () => {
    setForm({
      name: '',
      patterns: [''],
      action: 'navigate',
      target: '',
      description: '',
      params: {}
    });
    setIsCreating(false);
    setEditingId(null);
  };

  const handleSave = async () => {
    if (!form.name || !form.patterns[0] || !form.target) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const data = {
        ...form,
        patterns: form.patterns.filter(p => p.trim())
      };

      if (editingId) {
        await agenticApi.updateCustomCommand(editingId, data, headers);
        toast.success('Command updated');
      } else {
        await agenticApi.createCustomCommand(data, headers);
        toast.success('Command created');
      }
      
      resetForm();
      loadCommands();
    } catch (err) {
      console.error('Save failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to save command');
    }
  };

  const handleEdit = (cmd) => {
    setForm({
      name: cmd.name,
      patterns: cmd.patterns.length ? cmd.patterns : [''],
      action: cmd.action,
      target: cmd.target,
      description: cmd.description,
      params: cmd.params || {}
    });
    setEditingId(cmd.id);
    setIsCreating(true);
  };

  const handleDelete = async (commandId) => {
    if (!confirm('Are you sure you want to delete this command?')) return;
    
    try {
      await agenticApi.deleteCustomCommand(commandId, headers);
      toast.success('Command deleted');
      loadCommands();
    } catch (err) {
      toast.error('Failed to delete command');
    }
  };

  const handleToggleActive = async (cmd) => {
    try {
      await agenticApi.updateCustomCommand(cmd.id, { is_active: !cmd.is_active }, headers);
      loadCommands();
    } catch (err) {
      toast.error('Failed to update command');
    }
  };

  const addFromSuggestion = (suggestion) => {
    setForm({
      name: suggestion.name,
      patterns: suggestion.patterns || [''],
      action: suggestion.action,
      target: suggestion.target,
      description: suggestion.description,
      params: suggestion.params || {}
    });
    setIsCreating(true);
    setShowSuggestions(false);
  };

  const addPattern = () => {
    setForm(f => ({ ...f, patterns: [...f.patterns, ''] }));
  };

  const updatePattern = (idx, value) => {
    setForm(f => ({
      ...f,
      patterns: f.patterns.map((p, i) => i === idx ? value : p)
    }));
  };

  const removePattern = (idx) => {
    if (form.patterns.length > 1) {
      setForm(f => ({
        ...f,
        patterns: f.patterns.filter((_, i) => i !== idx)
      }));
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'navigate': return <ArrowRight className="w-4 h-4" />;
      case 'search': return <Search className="w-4 h-4" />;
      default: return <Target className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <Card data-testid="custom-commands-manager">
        <CardContent className="flex justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="custom-commands-manager">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Mic className="w-5 h-5 text-blue-600" />
              Custom Voice Commands
            </CardTitle>
            <CardDescription>
              Train custom voice commands for your organization
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSuggestions(!showSuggestions)}
            >
              <Sparkles className="w-4 h-4 mr-1" />
              Suggestions
            </Button>
            <Button
              size="sm"
              onClick={() => setIsCreating(true)}
              disabled={isCreating}
              data-testid="create-command-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              New Command
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Suggestions Panel */}
        {showSuggestions && suggestions.length > 0 && (
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="font-medium text-blue-800 mb-3 text-sm">
              Suggested Commands
            </h4>
            <div className="grid grid-cols-2 gap-2">
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  onClick={() => addFromSuggestion(s)}
                  className="text-left p-2 bg-white rounded border border-blue-100 hover:border-blue-300 transition-colors"
                >
                  <span className="font-medium text-sm text-blue-900">{s.name}</span>
                  <span className="text-xs text-slate-500 block">{s.description}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Create/Edit Form */}
        {isCreating && (
          <div className="p-4 bg-slate-50 rounded-lg border space-y-4">
            <div className="flex justify-between items-center">
              <h4 className="font-medium">
                {editingId ? 'Edit Command' : 'New Command'}
              </h4>
              <Button variant="ghost" size="sm" onClick={resetForm}>
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Command Name *</Label>
                <Input
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g., Show HR Documents"
                  data-testid="command-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Action Type *</Label>
                <Select
                  value={form.action}
                  onValueChange={v => setForm(f => ({ ...f, action: v }))}
                >
                  <SelectTrigger data-testid="command-action-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="navigate">Navigate to Page</SelectItem>
                    <SelectItem value="search">Search Documents</SelectItem>
                    <SelectItem value="api_call">API Call</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Trigger Phrases *</Label>
              <p className="text-xs text-slate-500">
                Add phrases users can say to trigger this command
              </p>
              {form.patterns.map((pattern, idx) => (
                <div key={idx} className="flex gap-2">
                  <Input
                    value={pattern}
                    onChange={e => updatePattern(idx, e.target.value)}
                    placeholder={`e.g., "show hr docs", "hr documents"`}
                    data-testid={`pattern-input-${idx}`}
                  />
                  {form.patterns.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removePattern(idx)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={addPattern}
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Pattern
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Target *</Label>
                <Input
                  value={form.target}
                  onChange={e => setForm(f => ({ ...f, target: e.target.value }))}
                  placeholder={form.action === 'navigate' ? '/documents' : 'search query'}
                  data-testid="command-target-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="What this command does"
                  data-testid="command-description-input"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={resetForm}>
                Cancel
              </Button>
              <Button onClick={handleSave} data-testid="save-command-btn">
                <Save className="w-4 h-4 mr-1" />
                {editingId ? 'Update' : 'Create'} Command
              </Button>
            </div>
          </div>
        )}

        {/* Commands List */}
        {commands.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <Mic className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p>No custom commands yet</p>
            <p className="text-sm">Create commands to personalize voice controls</p>
          </div>
        ) : (
          <div className="space-y-2">
            {commands.map(cmd => (
              <div
                key={cmd.id}
                data-testid={`command-item-${cmd.id}`}
                className={`p-3 rounded-lg border ${
                  cmd.is_active ? 'bg-white' : 'bg-slate-50 opacity-60'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {getActionIcon(cmd.action)}
                      <span className="font-medium">{cmd.name}</span>
                      <Badge variant={cmd.is_active ? "default" : "secondary"} className="text-xs">
                        {cmd.action}
                      </Badge>
                      {cmd.is_custom && (
                        <Badge variant="outline" className="text-xs">Custom</Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-600">{cmd.description}</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {cmd.patterns.map((p, i) => (
                        <span
                          key={i}
                          className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded"
                        >
                          "{p}"
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={cmd.is_active}
                      onCheckedChange={() => handleToggleActive(cmd)}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(cmd)}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(cmd.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CustomCommandsManager;
